import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const PrivacyPolicy = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-background text-foreground py-24 px-6 md:px-12 lg:px-24"
    >
      <Helmet>
        <title>Privacy Policy - MCubes</title>
        <meta name="description" content="Learn about MCubes' privacy policy regarding your data and information." />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-extrabold text-primary mb-8 text-center">
          Privacy Policy
        </h1>

        <section className="mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-4">1. Introduction</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            MCubes, operating in the Philippines, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <span className="font-semibold text-foreground">[Your Website URL Here]</span>, including any other media form, media channel, mobile website, or mobile application related or connected thereto (collectively, the "Site"). Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the Site.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates. You will be deemed to have been made aware of, will be subject to, and will be deemed to have accepted the changes in any revised Privacy Policy by your continued use of the Site after the date such revised Privacy Policy is posted.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-4">2. Collection of Your Information</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We may collect information about you in a variety of ways. The information we may collect on the Site includes:
          </p>
          <h3 className="text-xl font-semibold text-foreground mb-2">Personal Data</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Personally identifiable information, such as your name, shipping address, email address, and telephone number, and demographic information, such as your age, gender, hometown, and interests, that you voluntarily give to us when you register with the Site or when you choose to participate in various activities related to the Site, such as online chat and message boards. You are under no obligation to provide us with personal information of any kind, however, your refusal to do so may prevent you from using certain features of the Site.
          </p>
          <h3 className="text-xl font-semibold text-foreground mb-2">Derivative Data</h3>
          <p className="text-muted-foreground leading-relaxed">
            Information our servers automatically collect when you access the Site, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Site. If you are using our mobile application, this information may also include your device name and type, your operating system, your phone number, your country, your likes and replies to a post, and other interactions with the application and other users via server log files, as well as any other information you choose to provide.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-4">3. Use of Your Information</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Site to:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
            <li>Create and manage your account.</li>
            <li>Email you regarding your account or order.</li>
            <li>Enable user-to-user communications.</li>
            <li>Fulfill and manage purchases, orders, payments, and other transactions related to the Site.</li>
            <li>Generate a personal profile about you to make your visit to the Site more personalized.</li>
            <li>Increase the efficiency and operation of the Site.</li>
            <li>Monitor and analyze usage and trends to improve your experience with the Site.</li>
            <li>Notify you of updates to the Site.</li>
            <li>Offer new products, services, mobile applications, and/or recommendations to you.</li>
            <li>Perform other business activities as needed.</li>
            <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity.</li>
            <li>Request feedback and contact you about your use of the Site.</li>
            <li>Resolve disputes and troubleshoot problems.</li>
            <li>Respond to product and customer service requests.</li>
            <li>Send you a newsletter or other updates.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-4">4. Disclosure of Your Information</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
          </p>
          <h3 className="text-xl font-semibold text-foreground mb-2">By Law or to Protect Rights</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, or safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
          </p>
          <h3 className="text-xl font-semibold text-foreground mb-2">Third-Party Service Providers</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We may share your information with third parties that perform services for us or on our behalf, including data analysis, email delivery, hosting services, customer service, and marketing assistance.
          </p>
          <h3 className="text-xl font-semibold text-foreground mb-2">Marketing Communications</h3>
          <p className="text-muted-foreground leading-relaxed">
            With your consent, or with an opportunity for you to withdraw consent, we may share your information with third parties for marketing purposes, as permitted by law.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-4">5. Security of Your Information</h2>
          <p className="text-muted-foreground leading-relaxed">
            We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-4">6. Policy for Children</h2>
          <p className="text-muted-foreground leading-relaxed">
            We do not knowingly solicit information from or market to children under the age of 13. If you become aware of any data we have collected from children under age 13, please contact us using the contact information provided below.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-4">7. Contact Us</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you have questions or comments about this Privacy Policy, please contact us at:
          </p>
          <p className="text-foreground font-semibold">
            MCubes Team
          </p>
          <p className="text-muted-foreground">
            Email: mcubesevents@gmail.com
          </p>
        </section>

        <p className="text-muted-foreground text-sm mt-12 text-center">
          Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicy; 